import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import type { CustomerSensor } from '@metron/shared';
import { createLogger, toErrorMessage } from '@metron/shared';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../ui/sheet.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Checkbox } from '../ui/checkbox.tsx';
import { ConsumerMeterPicker } from './ConsumerMeterPicker.tsx';
import { getStatusBadge } from './statusBadge.tsx';
import {
  type AddEndConsumerResponse,
  type ConsumerMeter,
  type EndConsumerListItem,
  useConsumerMeters,
  useEndConsumers,
} from '../../hooks/useEndConsumers.ts';
import { useApiClient } from '../../hooks/useApiClient.ts';

const logger = createLogger('EndConsumerDrawer');

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EndConsumerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  consumer?: EndConsumerListItem | null;
  onRequestRemove?: (consumer: EndConsumerListItem) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

// ---------------------------------------------------------------------------
// CreateMode — local meter list before consumer exists
// ---------------------------------------------------------------------------

interface PendingMeter {
  sensorId: number;
  deviceId: string;
  address: string | null;
}

interface CreateModeProps {
  sensors: CustomerSensor[];
  sensorsLoading: boolean;
  onClose: () => void;
  addEndConsumer: (
    args: {
      email: string;
      meterIds: number[];
      name?: string;
      moveInDate?: string;
      sendInviteEmail?: boolean;
    },
  ) => Promise<AddEndConsumerResponse>;
}

function CreateMode({
  sensors,
  sensorsLoading,
  onClose,
  addEndConsumer,
}: CreateModeProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [moveInDate, setMoveInDate] = useState('');
  const [sendInviteEmail, setSendInviteEmail] = useState(false);
  const [pendingMeters, setPendingMeters] = useState<PendingMeter[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const existingSensorIds = useMemo(
    () => new Set(pendingMeters.map((m) => m.sensorId)),
    [pendingMeters],
  );

  const canSubmit = isValidEmail(email) && pendingMeters.length > 0 && !isSaving;

  const handlePickerAdd = (sensorIds: number[]) => {
    const added: PendingMeter[] = sensorIds
      .filter((id) => !existingSensorIds.has(id))
      .map((id) => {
        const sensor = sensors.find((s) => s.sensorId === id);
        return {
          sensorId: id,
          deviceId: sensor?.deviceId ?? String(id),
          address: sensor?.address ?? null,
        };
      });
    setPendingMeters((prev) => [...prev, ...added]);
  };

  const handleRemovePending = (sensorId: number) => {
    setPendingMeters((prev) => prev.filter((m) => m.sensorId !== sensorId));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSaving(true);
    try {
      const result = await addEndConsumer({
        email: email.trim(),
        meterIds: pendingMeters.map((m) => m.sensorId),
        name: name.trim() || undefined,
        moveInDate: moveInDate || undefined,
        sendInviteEmail,
      });
      const failedEmail = result.inviteEmails?.find((r) => !r.sent);
      if (failedEmail) {
        // The consumer was created; only the email failed.
        toast.warning(`Consumer added, but the onboarding email failed: ${failedEmail.error}`);
      } else if (result.inviteEmails) {
        toast.success('Consumer added and onboarding email sent');
      } else {
        toast.success('Consumer added successfully');
      }
      onClose();
    } catch (error) {
      const message = toErrorMessage(error, 'Failed to add consumer');
      toast.error(message);
      logger.error({ error }, 'Failed to add consumer');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle>Add consumer</SheetTitle>
      </SheetHeader>

      <div className='flex flex-col gap-4 py-4 flex-1 overflow-y-auto'>
        <div className='grid gap-2'>
          <Label htmlFor='create-email'>Email</Label>
          <Input
            id='create-email'
            type='email'
            placeholder='consumer@example.com'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSaving}
          />
        </div>

        <div className='grid gap-2'>
          <Label htmlFor='create-name'>Name (optional)</Label>
          <Input
            id='create-name'
            type='text'
            placeholder='Jane Doe'
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSaving}
          />
        </div>

        <div className='grid gap-2'>
          <Label htmlFor='create-move-in'>Move-in date (optional)</Label>
          <Input
            id='create-move-in'
            type='date'
            value={moveInDate}
            onChange={(e) => setMoveInDate(e.target.value)}
            disabled={isSaving}
          />
          <p className='text-sm text-muted-foreground'>Applies to all meters below.</p>
        </div>

        <div className='flex items-start gap-2'>
          <Checkbox
            id='create-send-invite'
            checked={sendInviteEmail}
            onCheckedChange={(checked) => setSendInviteEmail(checked === true)}
            disabled={isSaving}
          />
          <div className='grid gap-1'>
            <Label htmlFor='create-send-invite'>Send onboarding email</Label>
            <p className='text-sm text-muted-foreground'>
              Emails the consumer a link to sign up for MyWaterScope.
            </p>
          </div>
        </div>

        <div className='grid gap-2'>
          <div className='flex items-center justify-between'>
            <Label>Meters</Label>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => setPickerOpen(true)}
              disabled={isSaving || sensorsLoading}
            >
              + Add meters
            </Button>
          </div>

          {pendingMeters.length === 0
            ? (
              <p className='text-sm text-muted-foreground'>
                At least one meter is required.
              </p>
            )
            : (
              <ul className='divide-y border rounded-md' aria-label='Selected meters'>
                {pendingMeters.map((meter) => (
                  <li
                    key={meter.sensorId}
                    className='flex items-center justify-between px-3 py-2 text-sm'
                  >
                    <span>
                      <span className='font-mono'>{meter.deviceId}</span>
                      {meter.address && (
                        <span className='text-muted-foreground ml-2'>{meter.address}</span>
                      )}
                    </span>
                    <button
                      type='button'
                      aria-label={`Remove ${meter.deviceId}`}
                      className='text-muted-foreground hover:text-destructive'
                      onClick={() => handleRemovePending(meter.sensorId)}
                      disabled={isSaving}
                    >
                      <X className='h-4 w-4' />
                    </button>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>

      <SheetFooter>
        <Button type='button' variant='outline' onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button type='button' onClick={handleSubmit} disabled={!canSubmit}>
          {isSaving ? 'Adding...' : 'Add consumer'}
        </Button>
      </SheetFooter>

      <ConsumerMeterPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        sensors={sensors}
        existingSensorIds={existingSensorIds}
        onAdd={handlePickerAdd}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// EditMode — live meter list from useConsumerMeters
// ---------------------------------------------------------------------------

interface EditModeProps {
  customerId: string;
  consumer: EndConsumerListItem;
  sensors: CustomerSensor[];
  sensorsLoading: boolean;
  onClose: () => void;
  onRequestRemove: (consumer: EndConsumerListItem) => void;
  patchEndConsumer: (
    email: string,
    patch: { email?: string; enabled?: boolean; name?: string },
  ) => Promise<unknown>;
  addMeters: (args: { email: string; meterIds: number[] }) => Promise<unknown>;
  removeMeter: (args: { email: string; sensorId: number }) => Promise<unknown>;
  patchMeterMoveIn: (
    email: string,
    sensorId: number,
    moveInDate: string | null,
  ) => Promise<unknown>;
}

function EditMode({
  customerId,
  consumer,
  sensors,
  sensorsLoading,
  onClose,
  onRequestRemove,
  patchEndConsumer,
  addMeters,
  removeMeter,
  patchMeterMoveIn,
}: EditModeProps) {
  const [email, setEmail] = useState(consumer.email);
  const [name, setName] = useState(consumer.name ?? '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [meterPending, setMeterPending] = useState(false);

  const { data: metersData, isLoading: metersLoading } = useConsumerMeters(
    customerId,
    consumer.email,
  );
  const meters: ConsumerMeter[] = metersData?.meters ?? [];

  const existingSensorIds = useMemo(
    () => new Set(meters.map((m) => m.sensorId)),
    [meters],
  );

  const emailEdited = email.trim() !== consumer.email;
  const emailValid = isValidEmail(email);
  const nameChanged = name.trim() !== (consumer.name ?? '');
  // Save when something changed, and if the email was edited it must be valid.
  const canSave = !isSaving && !isPending &&
    (nameChanged || (emailEdited && emailValid)) &&
    (!emailEdited || emailValid);

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const patch: { email?: string; name?: string } = {};
      if (emailEdited && emailValid) patch.email = email.trim();
      if (nameChanged) patch.name = name.trim();
      await patchEndConsumer(consumer.email, patch);
      toast.success('Consumer updated successfully');
      onClose();
    } catch (error) {
      const message = toErrorMessage(error, 'Failed to update consumer');
      toast.error(message);
      logger.error({ error }, 'Failed to update consumer');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisable = async () => {
    setIsPending(true);
    try {
      await patchEndConsumer(consumer.email, { enabled: false });
      toast.success('Consumer disabled');
      onClose();
    } catch (error) {
      const message = toErrorMessage(error, 'Failed to disable consumer');
      toast.error(message);
      logger.error({ error }, 'Failed to disable consumer');
    } finally {
      setIsPending(false);
    }
  };

  const handleEnable = async () => {
    setIsPending(true);
    try {
      await patchEndConsumer(consumer.email, { enabled: true });
      toast.success('Consumer enabled');
      onClose();
    } catch (error) {
      const message = toErrorMessage(error, 'Failed to enable consumer');
      toast.error(message);
      logger.error({ error }, 'Failed to enable consumer');
    } finally {
      setIsPending(false);
    }
  };

  const handleMoveInChange = async (sensorId: number, value: string) => {
    try {
      await patchMeterMoveIn(consumer.email, sensorId, value === '' ? null : value);
      toast.success('Move-in date updated');
    } catch (error) {
      toast.error(toErrorMessage(error, 'Failed to update move-in date'));
      logger.error({ error }, 'Failed to update move-in date');
    }
  };

  const handleRemoveMeter = async (sensorId: number) => {
    setMeterPending(true);
    try {
      await removeMeter({ email: consumer.email, sensorId });
    } catch (error) {
      const message = toErrorMessage(error, 'Failed to remove meter');
      toast.error(message);
      logger.error({ error }, 'Failed to remove meter');
    } finally {
      setMeterPending(false);
    }
  };

  const handlePickerAdd = async (sensorIds: number[]) => {
    setMeterPending(true);
    try {
      await addMeters({ email: consumer.email, meterIds: sensorIds });
    } catch (error) {
      const message = toErrorMessage(error, 'Failed to add meters');
      toast.error(message);
      logger.error({ error }, 'Failed to add meters');
    } finally {
      setMeterPending(false);
    }
  };

  const busy = isSaving || isPending;
  const metersBusy = busy || meterPending;

  return (
    <>
      <SheetHeader>
        <SheetTitle>Edit consumer</SheetTitle>
      </SheetHeader>

      <div className='flex flex-col gap-4 py-4 flex-1 overflow-y-auto'>
        <div className='grid gap-2'>
          <Label htmlFor='edit-email'>Email</Label>
          <Input
            id='edit-email'
            type='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        </div>

        <div className='grid gap-2'>
          <Label htmlFor='edit-name'>Name (optional)</Label>
          <Input
            id='edit-name'
            type='text'
            placeholder='Jane Doe'
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
        </div>

        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium'>Status:</span>
          {getStatusBadge(consumer.status)}
        </div>

        <div className='grid gap-2'>
          <div className='flex items-center justify-between'>
            <Label>Meters</Label>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => setPickerOpen(true)}
              disabled={metersBusy || sensorsLoading}
            >
              + Add meters
            </Button>
          </div>

          {metersLoading
            ? (
              <div className='flex items-center justify-center py-4' role='status'>
                <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-primary' />
              </div>
            )
            : meters.length === 0
            ? <p className='text-sm text-muted-foreground'>No meters assigned.</p>
            : (
              <ul className='divide-y border rounded-md' aria-label='Assigned meters'>
                {meters.map((meter) => (
                  <li
                    key={meter.sensorId}
                    className='flex items-center justify-between px-3 py-2 text-sm'
                  >
                    <span>
                      <span className='font-mono'>{meter.deviceId}</span>
                      {meter.address && (
                        <span className='text-muted-foreground ml-2'>{meter.address}</span>
                      )}
                    </span>
                    <div className='flex items-center gap-2'>
                      <input
                        type='date'
                        aria-label={`Move-in date for ${meter.deviceId}`}
                        className='border rounded px-2 py-1 text-sm'
                        defaultValue={meter.moveInDate ?? ''}
                        disabled={metersBusy}
                        onChange={(e) => handleMoveInChange(meter.sensorId, e.target.value)}
                      />
                      <button
                        type='button'
                        aria-label={`Remove ${meter.deviceId}`}
                        className='text-muted-foreground hover:text-destructive'
                        onClick={() => handleRemoveMeter(meter.sensorId)}
                        disabled={metersBusy}
                      >
                        <X className='h-4 w-4' />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>

      <SheetFooter className='flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex gap-2'>
          {consumer.status === 'active' && (
            <Button
              type='button'
              variant='outline'
              onClick={handleDisable}
              disabled={busy}
            >
              {isPending ? 'Disabling...' : 'Disable'}
            </Button>
          )}
          {consumer.status === 'disabled' && (
            <Button
              type='button'
              variant='outline'
              onClick={handleEnable}
              disabled={busy}
            >
              {isPending ? 'Enabling...' : 'Enable'}
            </Button>
          )}
          <Button
            type='button'
            variant='destructive'
            onClick={() => onRequestRemove(consumer)}
            disabled={busy}
          >
            Remove
          </Button>
        </div>
        <div className='flex gap-2 sm:ml-auto'>
          <Button type='button' variant='outline' onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type='button' onClick={handleSave} disabled={!canSave}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </SheetFooter>

      <ConsumerMeterPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        sensors={sensors}
        existingSensorIds={existingSensorIds}
        onAdd={handlePickerAdd}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// EndConsumerDrawer — root
// ---------------------------------------------------------------------------

export function EndConsumerDrawer({
  open,
  onOpenChange,
  customerId,
  consumer,
  onRequestRemove,
}: EndConsumerDrawerProps) {
  const apiClient = useApiClient();
  const {
    addEndConsumer,
    patchEndConsumer,
    addMeters,
    removeMeter,
    patchMeterMoveIn,
  } = useEndConsumers(customerId);

  const { data: sensorsData, isLoading: sensorsLoading } = useQuery({
    queryKey: ['customer-sensors', customerId] as const,
    queryFn: () => apiClient.get<{ sensors: CustomerSensor[] }>(`customers/${customerId}/sensors`),
    enabled: open && !!customerId,
  });
  const sensors = sensorsData?.sensors ?? [];

  const handleClose = () => onOpenChange(false);

  const handleRequestRemove = (c: EndConsumerListItem) => {
    onOpenChange(false);
    onRequestRemove?.(c);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='flex flex-col overflow-hidden sm:max-w-md'>
        {consumer
          ? (
            <EditMode
              key={consumer.email}
              customerId={customerId}
              consumer={consumer}
              sensors={sensors}
              sensorsLoading={sensorsLoading}
              onClose={handleClose}
              onRequestRemove={handleRequestRemove}
              patchEndConsumer={patchEndConsumer}
              addMeters={addMeters}
              removeMeter={removeMeter}
              patchMeterMoveIn={patchMeterMoveIn}
            />
          )
          : (
            <CreateMode
              key='create'
              sensors={sensors}
              sensorsLoading={sensorsLoading}
              onClose={handleClose}
              addEndConsumer={addEndConsumer}
            />
          )}
      </SheetContent>
    </Sheet>
  );
}
